import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { SECURITY_DEFAULTS, type MfaPolicy } from "@/lib/securityPolicy";

export interface MfaRequirement {
  policy: MfaPolicy;
  required: boolean;
  enrolled: boolean;
  gracePeriodDays: number;
}

/**
 * Resolves the organization MFA policy for the signed-in user.
 * Evaluated server-side (SECURITY DEFINER) so the client cannot spoof
 * its role or enrollment state.
 */
export function useMfaPolicy() {
  const { user } = useAuth();

  const { data, isLoading } = useQuery({
    queryKey: ["mfa-requirement", user?.id],
    queryFn: async (): Promise<MfaRequirement> => {
      const { data, error } = await supabase.rpc("mfa_requirement_for_user", {
        _user_id: user!.id,
      });
      if (error) throw error;
      const row = Array.isArray(data) ? data[0] : data;
      return {
        policy: (row?.policy as MfaPolicy) ?? SECURITY_DEFAULTS.mfaPolicy,
        required: !!row?.required,
        enrolled: !!row?.enrolled,
        gracePeriodDays: row?.grace_period_days ?? SECURITY_DEFAULTS.mfaGracePeriodDays,
      };
    },
    enabled: !!user?.id,
    staleTime: 60 * 1000,
    retry: false,
  });

  return {
    policy: data?.policy ?? SECURITY_DEFAULTS.mfaPolicy,
    mfaRequired: data?.required ?? false,
    mfaEnrolled: data?.enrolled ?? false,
    gracePeriodDays: data?.gracePeriodDays ?? SECURITY_DEFAULTS.mfaGracePeriodDays,
    /** True when the user must enroll before continuing. */
    mustEnroll: !!data?.required && !data?.enrolled,
    isLoading,
  };
}
